// nebuleaTheme.ts
// MUI v4 theme (Material-UI) using createMuiTheme from '@material-ui/core/styles'

import { createMuiTheme } from "@material-ui/core/styles";

const nebulea = {
  // Brand
  primary: "#FCEB26",          // Jaune Nebulea (primary)
  secondaryBlue: "#101526",    // Bleu Nebulea (secondary) -> ajuste si tu as un bleu exact

  // Light surfaces
  bg: "#F6F7F9",               // fond global app
  surface: "#FFFFFF",          // surfaces (Paper/Card/Dialog)
  surface2: "#FFFFFF",

  // Borders on light
  border: "rgba(17,24,39,0.10)",       // ~ slate/gray
  borderHover: "rgba(17,24,39,0.18)",

  // Text on light
  text: "#111827",
  textMuted: "#6B7280",

  success: "#16A34A",
  warning: "#F59E0B",
  error: "#EF4444",

  // Subtle glow (très léger, compatible light)
  glow1: "rgba(252,235,38,0.18)", // jaune très soft
  glow2: "rgba(30,58,138,0.06)",  // bleu très soft
};

export const nebuleaTheme = createMuiTheme({
  palette: {
    type: "light",

    primary: {
      main: nebulea.primary,
      // sur du jaune, texte sombre
      contrastText: "#101526",
    },

    secondary: {
      main: nebulea.secondaryBlue,
      contrastText: "#FFFFFF",
    },

    background: {
      default: nebulea.bg,
      paper: nebulea.surface, // blanc
    },

    text: {
      primary: nebulea.text,
      secondary: nebulea.textMuted,
    },

    divider: nebulea.border,

    success: { main: nebulea.success },
    warning: { main: nebulea.warning },
    error: { main: nebulea.error },
  },

  shape: {
    borderRadius: 14,
  },

  typography: {
    // Body
    fontFamily: '"Source Sans Pro", sans-serif',

    // Titles
    h1: {
      fontFamily: '"Titillium Web", sans-serif',
      fontWeight: 700,
      fontSize: "2rem",
      letterSpacing: "-0.02em",
    },
    h2: {
      fontFamily: '"Titillium Web", sans-serif',
      fontWeight: 700,
      fontSize: "1.5rem",
      letterSpacing: "-0.015em",
    },
    h3: {
      fontFamily: '"Titillium Web", sans-serif',
      fontWeight: 600,
      fontSize: "1.25rem",
      letterSpacing: "-0.01em",
    },
    h6: {
      fontFamily: '"Titillium Web", sans-serif',
      fontWeight: 600,
      textTransform: 'uppercase',
      fontStyle: 'italic',
      fontSize: "1rem",
      lineHeight: 1.14,
    },

    body1: { fontSize: "0.875rem" },
    body2: { fontSize: "0.8125rem" },

    button: {
      textTransform: "none",
      fontWeight: 600,
    },
  },

  overrides: {
    MuiCssBaseline: {
      "@global": {
        body: {
          backgroundColor: nebulea.bg,
          backgroundImage: `
            radial-gradient(700px 420px at 18% 8%, ${nebulea.glow1} 0%, transparent 62%),
            radial-gradient(900px 620px at 88% 18%, ${nebulea.glow2} 0%, transparent 60%)
          `,
          backgroundRepeat: "no-repeat",
        },
      },
    },

    MuiPaper: {
      root: {
        backgroundImage: "none",
        backgroundColor: nebulea.surface,
        border: `1px solid ${nebulea.border}`,
      },
      rounded: {
        borderRadius: 14,
      },
    },

    MuiCard: {
      root: {
        border: `1px solid ${nebulea.border}`,
        boxShadow: "0 10px 30px rgba(17,24,39,0.08)",
      },
    },

    MuiAppBar: {
      colorPrimary: {
        backgroundColor: nebulea.surface,
        color: nebulea.text,
        borderBottom: `1px solid ${nebulea.border}`,
      },
    },

    MuiButton: {
      root: {
        borderRadius: 12,
      },
      contained: {
        boxShadow: "none",
      },
      containedPrimary: {
        backgroundColor: nebulea.primary,
        color: "#101526",
        "&:hover": {
          backgroundColor: "#E6D822",
        },
      },
      containedSecondary: {
        backgroundColor: nebulea.secondaryBlue,
        color: "#FFFFFF",
        "&:hover": {
          backgroundColor: "#172B66",
        },
      },
      outlined: {
        borderColor: nebulea.border,
        "&:hover": {
          borderColor: nebulea.borderHover,
          backgroundColor: "rgba(17,24,39,0.03)",
        },
      },
      text: {
        "&:hover": {
          backgroundColor: "rgba(17,24,39,0.03)",
        },
      },
    },

    MuiOutlinedInput: {
      root: {
        borderRadius: 12,
        backgroundColor: "#FFFFFF",
        "&:hover $notchedOutline": {
          borderColor: nebulea.borderHover,
        },
        "&$focused $notchedOutline": {
          borderColor: nebulea.secondaryBlue, // focus plus lisible en light
          borderWidth: 1,
        },
      },
      notchedOutline: {
        borderColor: nebulea.border,
      },
    },

    MuiInputLabel: {
      root: {
        color: nebulea.textMuted,
        "&$focused": {
          color: nebulea.secondaryBlue,
        },
      },
    },

    MuiChip: {
      root: {
        borderRadius: 999,
      },
      outlined: {
        borderColor: nebulea.border,
      },
    },

    MuiDivider: {
      root: {
        backgroundColor: nebulea.border,
      },
    },

    MuiToggleButton: {
      root: {
        borderRadius: 999,
        borderColor: nebulea.border,
        color: nebulea.textMuted,
        "&:hover": {
          backgroundColor: "rgba(17,24,39,0.03)",
        },
        "&.Mui-selected": {
          backgroundColor: nebulea.primary,
          color: "#101526",
          "&:hover": {
            backgroundColor: "#E6D822",
          },
        },
      },
    },

    MuiTypography: {
      // ensure default color consistency for v4
      root: {
        color: nebulea.text,
      },
      h6: {
        color: '#FCEB26',
        backgroundColor: '#101526',
        borderRadius: 12,
        padding: '6px 12px',
        display: 'inline-block', // sinon le background prend toute la largeur
      },
    },
  },
});